import { useCallback } from 'react';
import { isTeamsHost, resolveDotstormingWebAuthToken } from '../services/webAuth';
import { apiRequest } from '../services/apiClient';
import type { Participant } from '../interfaces/Participant';
import useUserStore from '../store/userStore';
import { useTeamsViewerIdentity } from '../teams/hooks/useTeamsViewerIdentity';

type BoardViewerSession = {
  authToken?: string;
};

type CheckAuthenticatedResponse = {
  user?: {
    _id?: string;
    id?: string;
    name?: string;
  } | null;
};

const normalizeStoreUser = (input: {
  id: string | null | undefined;
  name: string | null | undefined;
}): Participant | null => {
  const id = input.id?.trim() ?? '';
  const name = input.name?.trim() ?? '';
  if (!id || !name) return null;

  return { id, name, votes: 0 };
};

const getAuthenticatedWebUser = async (authToken: string): Promise<Participant | null> => {
  try {
    const payload = await apiRequest<CheckAuthenticatedResponse>(
      '/users/checkAuthenticated',
      { method: 'GET' },
      { Authorization: `Bearer ${authToken}` }
    );

    return normalizeStoreUser({
      id: payload.user?._id ?? payload.user?.id,
      name: payload.user?.name,
    });
  } catch {
    return null;
  }
};

const getTeamsAuthFromSearch = (search: string): string | undefined => {
  const sp = new URLSearchParams(search);
  const token = sp.get('auth')?.trim();
  return token ? token : undefined;
};

export const useBoardViewerIdentity = () => {
  const { resolveViewerIdentity } = useTeamsViewerIdentity();

  const setCurrentUser = useUserStore((state) => state.setCurrentUser);

  const resolveBoardViewerSession = useCallback(
    async (search: string): Promise<BoardViewerSession> => {
      const inTeams = isTeamsHost();

      const teamsAuthToken = inTeams ? getTeamsAuthFromSearch(search) : undefined;
      const webAuthToken = (await resolveDotstormingWebAuthToken()) ?? undefined;
      const authToken = webAuthToken ?? teamsAuthToken;

      let chosenUser: Participant | null = null;
      if (webAuthToken) {
        chosenUser = await getAuthenticatedWebUser(webAuthToken);
      } else if (authToken && inTeams) {
        const identity = await resolveViewerIdentity();

        chosenUser = normalizeStoreUser({
          id: identity.participantId,
          name: identity.displayName,
        });
      }

      setCurrentUser(chosenUser, {
        isGuest: false,
      });

      return { authToken };
    },
    [resolveViewerIdentity, setCurrentUser]
  );

  return { resolveBoardViewerSession };
};
